import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { openContentDb } from './db';
import { syncContent, seedIfEmpty, readAllStores } from './contentLoader';
import { hydrateMirror } from './contentStore';
import { useOnline } from './connectivity';
import { syncUserDataWithRetry } from './syncEngine';
import { migrateLocalStorage, ensureDemoPreset } from './migrateLocalStorage';
import { attachUserDb, hydrateUserMirror } from './userStore';
import { getAllUserData } from './userDataStore';
import { getAccessToken } from '../lib/supabaseClient';
import { refreshPatreonEntitlement } from '../lib/patreon';
import { refreshBetaEntitlement, restoreBetaEntitlement, checkInvitedAccess } from '../lib/beta';
import { getPatreonEntitlement, getBetaEntitlement } from './entitlements';
import { whenPlaybackIdle } from '../utils/playbackActivity';
import { API_URL } from '../config/api';

const DataContext = createContext(null);

// Repli hors provider pour useSyncNow : il n'y a rien à synchroniser, mais
// l'appelant doit pouvoir enchaîner un .catch() sans se demander s'il tient
// une promesse.
const noopSyncNow = () => Promise.resolve();


// Wrap l'app : ouvre IndexedDB, lance la sync du content pack au boot,
// expose le handle DB + un statut. IndexedDB reste la source de verite de l'UI ;
// la sync ne fait que rafraichir, elle ne bloque jamais le rendu offline-first.
export function DataProvider({ children, locale = 'en' }) {
  const online = useOnline();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'offline' | 'error'
  // Vrai entre le demarrage d'une session connectee et la fin du premier pull
  // du compte. Sert a une seule chose : ne pas annoncer "aucun acces sur ce
  // compte" a quelqu'un dont l'acces est en train d'arriver. Le pull attend la
  // synchro du contenu (voir plus bas), ce qui laisse plusieurs secondes ou
  // l'app dit le contraire de la verite.
  const [accessSyncing, setAccessSyncing] = useState(false);
  const [db, setDb] = useState(null);
  const dbRef = useRef(null);
  // La synchro des données user est déclenchable hors de l'effet (après un
  // achat) : elle lit donc l'état courant par des refs, pas par la fermeture
  // de l'effet, qui n'existe pas au moment de l'achat.
  const onlineRef = useRef(online);
  const mountedRef = useRef(false);
  // Jeton d'annulation partagé : le nettoyage de l'effet l'incrémente, et
  // toute passe encore en vol se voit alors annulée (plus de setState ni de
  // re-hydratation après un démontage ou un changement de dépendances).
  const runRef = useRef(0);
  const syncingRef = useRef(false);
  const pendingRef = useRef(false);

  // Une passe complète : revalidations d'accès, push dirty + pull deltas,
  // re-hydratation du miroir. Ne jette jamais, les erreurs sont journalisées
  // comme avant.
  const runSyncPass = useCallback(async () => {
    const database = dbRef.current;
    if (!database) return;
    const run = runRef.current;
    const cancelled = () => runRef.current !== run;

    // Pose des maintenant, pas dans le then : l'attente qu'on habille
    // commence ici, avant meme la synchro du contenu.
    getAccessToken().then((tok) => {
      if (tok && !cancelled()) setAccessSyncing(true);
    });

    try {
      // whenPlaybackIdle : une reconnexion en pleine lecture ne doit pas
      // declencher push+pull+re-hydratation (re-render global) pendant le jam.
      await whenPlaybackIdle();
      if (cancelled()) return;
      // Revalidation Patreon AVANT le pull : le tombstone d'un membre
      // parti doit etre en base quand le pull passe, sinon la revocation
      // prend deux sessions au lieu d'une. No-op sans lien connu, et
      // silencieux en cas de panne (la fenetre de grace court alors).
      if (getPatreonEntitlement()) await refreshPatreonEntitlement();
      if (cancelled()) return;
      // Re-validation beta (throttle 24 h interne) : un code desactive en
      // Admin retire l'acces des la prochaine connexion. No-op sans code.
      if (getBetaEntitlement()) await refreshBetaEntitlement();
      // Sans acces : le serveur reconnait peut-etre l'adresse du compte
      // (invitation par mail). Il pose alors la row AVANT le pull qui
      // suit, et l'acces arrive dans la foulee. No-op en invite ou sous
      // throttle (24 h interne).
      else await checkInvitedAccess();
      if (cancelled()) return;

      const res = await syncUserDataWithRetry(database, {
        apiUrl: API_URL,
        online: onlineRef.current,
        getToken: getAccessToken,
      });
      if (res === null || cancelled()) return;
      // Après le pull, on re-hydrate le miroir pour refléter les changements
      // serveur. C'est ce passage qui fait bouger l'UI après un achat.
      hydrateUserMirror(await getAllUserData(database));
      // Second passage beta, apres le pull, deux cas :
      //  - une row est la (elle vient d'arriver du compte, sur un
      //    appareil qui n'a jamais rien valide) : le verify fait adopter
      //    l'appareil par le code, sans toucher au cap puisque le porteur
      //    ne change pas, et relance la fenetre de grace ;
      //  - aucune row mais un code memorise sur cette machine : la purge
      //    d'un changement de compte l'a emportee, le serveur reconnait
      //    toujours l'appareil, on la repose.
      // No-op si le throttle vient de passer, ou sans code du tout.
      const betaRes = getBetaEntitlement()
        ? await refreshBetaEntitlement()
        : await restoreBetaEntitlement();
      if (betaRes && !cancelled()) hydrateUserMirror(await getAllUserData(database));
    } catch (err) {
      console.error('[DataProvider] sync user data echouee:', err);
    } finally {
      if (!cancelled()) setAccessSyncing(false);
    }
  }, []);

  // Synchro des données user déclenchable depuis le contexte : un achat n'a
  // plus à attendre le prochain démarrage pour atteindre le serveur. No-op
  // sans base attachée ou hors ligne, et sans erreur : l'appelant n'a pas à
  // connaître l'état du boot.
  const syncNow = useCallback(async () => {
    if (!dbRef.current || !onlineRef.current) return;
    // Deux appels concurrents ne se marchent pas dessus : le second est
    // reporté à la fin du premier, pas perdu. Un achat conclu pendant le
    // backoff de la synchro de démarrage doit quand même partir.
    if (syncingRef.current) {
      pendingRef.current = true;
      return;
    }
    syncingRef.current = true;
    try {
      do {
        pendingRef.current = false;
        await runSyncPass();
      } while (pendingRef.current && mountedRef.current && onlineRef.current && dbRef.current);
    } finally {
      syncingRef.current = false;
      pendingRef.current = false;
    }
  }, [runSyncPass]);

  useEffect(() => {
    let cancelled = false;
    onlineRef.current = online;
    mountedRef.current = true;

    (async () => {
      try {
        const database = dbRef.current || (await openContentDb());
        dbRef.current = database;
        if (!cancelled) setDb(database);

        // Baseline IndexedDB depuis le pack bundle si vide (offline-first).
        await seedIfEmpty(database);

        // Donnees user : migration one-shot localStorage -> user_data, seed du
        // demo-preset, puis hydratation du miroir synchrone AVANT le premier
        // rendu (les consommateurs lisent le miroir des le montage).
        attachUserDb(database);
        await migrateLocalStorage(database);
        await ensureDemoPreset(database);
        hydrateUserMirror(await getAllUserData(database));

        // Rafraichit le contenu (manifeste -> delta) ; no-op si offline.
        // Try dédié : un échec du contenu ne doit pas emporter la synchro des
        // droits qui suit. Sans lui, une panne du serveur de contenu annonce
        // "offline" alors qu'on est en ligne, et laisse l'acheteur verrouillé.
        try {
          const res = await syncContent(database, { apiUrl: API_URL, online, locale });
          // Re-hydrate le miroir memoire depuis IndexedDB (baseline + deltas appliques).
          hydrateMirror(await readAllStores(database), { locale });
          if (!cancelled) setStatus(res.offline ? 'offline' : 'ready');
        } catch (err) {
          // Le reseau a echoue : on reste utilisable sur le cache existant.
          console.error('[DataProvider] sync content echouee, fallback cache:', err);
          // La baseline est déjà en base (seedIfEmpty) : on hydrate le miroir
          // quand même, sinon l'app tourne sans contenu alors qu'IndexedDB en a.
          try {
            hydrateMirror(await readAllStores(database), { locale });
          } catch (readErr) {
            console.error('[DataProvider] relecture du cache echouee:', readErr);
          }
          if (!cancelled) setStatus(dbRef.current ? 'offline' : 'error');
        }

        // Sync des donnees user en arriere-plan (push dirty + pull deltas).
        // Non bloquant : l'UI est deja rendue depuis IndexedDB. Re-declenche au
        // retour online via la dependance [online] du useEffect, et après un
        // achat via syncNow exposé dans le contexte.
        if (!cancelled && online) syncNow();
      } catch (err) {
        // Le reseau a echoue : on reste utilisable sur le cache existant.
        console.error('[DataProvider] sync content echouee, fallback cache:', err);
        if (!cancelled) setStatus(dbRef.current ? 'offline' : 'error');
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      runRef.current += 1;
    };
    // syncNow est stable et volontairement hors des dépendances : l'y mettre
    // ne changerait rien au comportement mais brouillerait la règle, cet effet
    // ne doit se rejouer que sur un changement de réseau ou de langue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, locale]);

  return (
    <DataContext.Provider value={{ db, status, online, locale, accessSyncing, syncNow }}>
      {children}
    </DataContext.Provider>
  );
}

export function useDataContext() {
  const ctx = useContext(DataContext);
  if (!ctx) {
    throw new Error('useDataContext doit etre utilise a l interieur de <DataProvider>.');
  }
  return ctx;
}

// Variante tolerante de useDataContext, pour les composants montes hors du
// provider dans les tests : le verrou en est un, et lui imposer un provider
// complet pour une seule information d'affichage serait un mauvais echange.
// Hors provider, personne n'est en train de synchroniser : false est la bonne
// reponse, pas une valeur par defaut arbitraire.
export function useAccessSyncing() {
  const ctx = useContext(DataContext);
  return !!ctx?.accessSyncing;
}

// Même tolérance pour le déclencheur de synchro : useEntitlement est monté par
// la feuille de verrou et la page You, toutes deux testées sans provider. Hors
// provider il n'y a ni base ni réseau à pousser, le no-op est la bonne réponse.
export function useSyncNow() {
  const ctx = useContext(DataContext);
  return ctx?.syncNow || noopSyncNow;
}
